/**
 * Shared activity engine, ported from PlayspaceBase.cs / PlayerElement.cs /
 * PlayerManager.cs.
 *
 *  - Every activity has a number of "items" to solve (numItems). Each correct
 *    item decrements it; when it reaches 0 the slide ends as correct.
 *  - Each wrong action adds one error. A wrong action that is not "always
 *    correct" (or any wrong action in a quiz project) ends the slide as
 *    incorrect and adds two more errors.
 *  - Stars for a slide (PlayerManager.GetStars): 0 errors → 3, 1–2 → 2, 3+ → 1.
 *  - Sound effects: "ok" on each solved item, "wrong" on each error
 *    (PlayerElement.cs → AudioPlayingController).
 *  - Points (quiz projects, AnswersCollector.cs): 500 for a correct answer
 *    without timer, or floor(900 * (1 - responseTime/seconds/2)) with timer.
 */
import { useCallback, useEffect, useRef } from "react";
import { usePlayer } from "./context";
import { playFx } from "./sounds";

export interface ActivityApi {
  /** Report one solved item. Ends the slide when no items remain. */
  correct: (answer?: string) => void;
  /** Report a mistake. `alwaysCorrect=false` ends the slide as incorrect. */
  wrong: (alwaysCorrect?: boolean, answer?: string) => void;
  /** End the slide right now (free-form activities such as paint). */
  done: (answer?: string) => void;
  /** Whether the slide has already ended. */
  isDone: () => boolean;
  /** Quiz project: every wrong answer ends the slide. */
  quizMode: boolean;
}

export function isQuizProject(type: string): boolean {
  return type === "quiz" || type === "quizDemo";
}

export function useActivity(items: number): ActivityApi {
  const { project, slide, finish, slideStartedAt, timeUp } = usePlayer();
  const remaining = useRef(items);
  const errors = useRef(0);
  const ended = useRef(false);
  const quizMode = isQuizProject(project.type);

  // Reset when the slide changes (components are keyed by slide id, but be safe).
  useEffect(() => {
    remaining.current = items;
    errors.current = 0;
    ended.current = false;
  }, [slide.id, items]);
  // Time ran out (quiz with timer): the player already recorded the slide as wrong.
  useEffect(() => {
    if (timeUp) ended.current = true;
  }, [timeUp]);

  const end = useCallback(
    (correct: boolean, answer?: string) => {
      if (ended.current) return;
      ended.current = true;
      const stars = starsForErrors(errors.current);
      let points = 0;
      if (quizMode && correct) {
        if (project.settings.timer === "timer") {
          // Timer.GetResponseTime(): seconds - floor(remaining)
          const seconds = slide.seconds > 0 ? slide.seconds : 20;
          const remaining = Math.max(0, seconds - (Date.now() - slideStartedAt) / 1000);
          const responseTime = seconds - Math.floor(remaining);
          const opTime = Math.min(1, responseTime / seconds) / 2;
          points = Math.floor(900 * (1 - opTime));
        } else {
          points = 500;
        }
      }
      finish({ correct, points, stars, answer, errors: errors.current });
    },
    [finish, project.settings.timer, slide.seconds, slideStartedAt, quizMode],
  );

  const correct = useCallback(
    (answer?: string) => {
      if (ended.current) return;
      remaining.current -= 1;
      playFx("ok", project.settings);
      if (remaining.current <= 0) end(true, answer);
    },
    [end, project.settings],
  );

  const wrong = useCallback(
    (alwaysCorrect = true, answer?: string) => {
      if (ended.current) return;
      errors.current += 1;
      playFx("wrong", project.settings);
      if (!alwaysCorrect || quizMode) {
        errors.current += 2;
        end(false, answer);
      }
    },
    [end, quizMode, project.settings],
  );

  const done = useCallback((answer?: string) => end(true, answer), [end]);
  const isDone = useCallback(() => ended.current, []);

  return { correct, wrong, done, isDone, quizMode };
}

/** PlayerManager.GetStars() */
export function starsForErrors(errors: number): number {
  if (errors === 0) return 3;
  if (errors < 3) return 2;
  return 1;
}

/** Quiz answer card colours, in order (GameManager.cs). */
export const QUIZ_COLORS = ["#fd9100", "#752992", "#06aae2", "#fb0000"];
export const COLOR_GREEN = "#39b54a";
export const COLOR_RED = "#fb0000";
export const COLOR_BLUE = "#06aae2";
